<template>
  <div class="flex-grow-1">
    <div v-if="isLoading" class="mt-4">
      <Spinner />
    </div>

    <a-alert
      message="About AI NLP Classifier"
      type="info"
      style="margin-bottom: 10px"
    >
      <div slot="description">
        <p>
          The AI NLP Classifier is a unique WebOrion micro-service that analyzes the HTML text given to it and returns a Defacement Confidence Score.  
          If the score exceeds the threshold (default 50%), an alert will be triggered.
        </p>
        <!-- <div v-if="canStartFreeTrial && !isLoading">
          Start your free trial to try out!
          <a-button type="primary" @click="startTrial">
            Start trial access
          </a-button>
        </div> -->
      </div>
    </a-alert>

    <a-checkbox 
      :checked="enable_nlp_deeplearning_engine && showEngine"
      :disabled="!showEngine"
      @change="change_enable_nlp_deeplearning_engine"
      @click.stop
      @change.stop
    >
      Enable AI NLP Classifier
    </a-checkbox>

    <a-alert 
      v-if="isEngineTrial && !isEngineSubscribed"
      :message="'Trial period till ' + formatDate(trialEndDate)"
      type="info"
      style="margin-bottom: 10px"
    >
      <p slot="description">
        We are excited to have you try out this new engine! Enable the checkboxes to monitor the content using the AI NLP Engine! <br>
        We will love to hear your thoughts. Please email <a href="mailto://custsupport@cloudsine.tech">
          custsupport@cloudsine.tech
        </a> for any questions or suggestions about this new feature.
      </p>
    </a-alert>

    <a-alert 
      v-if="isEngineSubscribed"
      message="AI NLP Engine Subscription"
      type="info"
      style="margin-bottom: 10px"
    >
      <p slot="description">
        We are excited to have you subscribed to this engine! Enable the checkboxes to analyze the HTML content using the AI NLP Engine! <br>
        We will love to hear your thoughts. Please email <a href="mailto://custsupport@cloudsine.tech">
          custsupport@cloudsine.tech
        </a> for any questions or suggestions about this new feature.
      </p>
    </a-alert>

    <a-alert 
      v-if="!showEngine"
      type="error"
      show-icon
      style="background-color: rgb(255, 242, 240); border: 1px solid rgb(255, 204, 199);padding-bottom: 8px;"
    >
      <template #icon>
        <a-icon type="warning" theme="filled" />
      </template>
      <!-- <div slot="message" style="color: #000000d9; font-size: 14px; font-weight: 600;">
        You are not subscribed to this engine!
      </div> -->
      <div slot="description" style="color: #000000d9; font-size: 12px">
        <p>
          You are currently not subscribed to this engine. Please contact us at
          <a href="mailto://sales@cloudsine.tech">
            sales@cloudsine.tech
          </a> 
          to begin your subscription. 
        </p>
      </div>
    </a-alert>

    <a-alert 
      v-if="policy_id > 0 && showEngine"
      type="warning"
      style="margin-bottom: 10px"
      show-icon
    >
      <template #icon>
        <a-icon type="warning" theme="filled" />
      </template>
      <div slot="message">
        <b>Warning</b>
      </div>
      <!-- <p v-if="nlpNotificationConfig && nlpNotificationConfig.enabled" slot="description"> -->
      <p v-if="show_model_setting" slot="description">
        A <a :href="'/policy/' + policy_id" target="_blank">
          policy
        </a> is associated with this URL, but this URL is following the URL configurations. You can click the button to 
        <a-button type="primary" @click="remove_url_configurations">
          use policy configuration
        </a-button>
      </p>
      
      <p v-else slot="description">
        This URL is currently already associated with a policy.
        To view and update the policy, please click <a :href="'/policy/' + policy_id" target="_blank">
          here
        </a>. However, 
        if you would like to override the policy for this specific URL,
        you may update the configurations directly on this page. 
      </p>
    </a-alert>

    <!-- Distilbert model -->
    <a-card v-if="!isLoading" title="AI NLP Classifier" style="margin-top: 20px; margin-bottom: 10px">
      <a-alert
        message="About Model T"
        type="info"
      >
        <p slot="description">
          This AI NLP Classifier is pre-trained on >10 years of defaced and non-defaced data using a proven deep learning Transformer Model.
          We have nicknamed it Model T.
        </p>
      </a-alert>
      <NLPModel
        v-if="distilbert_model.enabled && enable_nlp_deeplearning_engine && showEngine"
        ref="distilbertRef"
        :model="distilbert_model"
        style="padding-left: 0 10px"
        @change="updateParent"
        @save-config="save_config"
      />
    </a-card>

    <a-row v-if="!isLoading" :gutter="20">
      <a-col :span="24" class="nlp-engine-collapse">
        <a-collapse expand-icon-position="left" style="margin-top: 10px">
          <a-collapse-panel key="xgboost" header="Advanced Settings">
            <a-alert
              v-if="!isLoading"
              message="Alert policy"
              type="warning"
              style="margin-bottom: 10px"
            >
              <p slot="description">
                Enable alert triggers based on
                <a-select v-model="notification_policy" style="width: 240px" :disabled="!enable_nlp_deeplearning_engine || !showEngine" @change="update_notification_policy">
                  <a-select-option value="DistilBERT+">
                    Model T only
                  </a-select-option>
                  <a-select-option value="Either">
                    Either Model T or Model X
                  </a-select-option>
                  <a-select-option value="Both">
                    Both Model T and Model X
                  </a-select-option>
                  <a-select-option value="XGB">
                    Model X only
                  </a-select-option>
                </a-select>
              </p>
            </a-alert>
            <a-alert
              message="About Model X"
              type="info"
            >
              <p slot="description">
                This model uses a different AI technique based on frequency of words that appeared in past 10 years of defacements. 
                It is optional and can be used to complement Model Y to improve defacement classifications.
              </p>
            </a-alert>
            <NLPModel
              v-if="xgboost_model.enabled && enable_nlp_deeplearning_engine && showEngine"
              ref="xgboostRef"
              :model="xgboost_model"
              style="padding: 0 10px"
              @change="updateParent"
              @save-config="save_config"
            />
          </a-collapse-panel>
        </a-collapse>
      </a-col>
    </a-row>

    <a-row :gutter="20">
      <a-col :span="24">
        <!-- Add a little bit of space at the end of the page -->
        <br><br>
      </a-col>
    </a-row>
  </div>
</template>

<style lang="scss">
.nlp-engine-collapse
  .ant-collapse
  > .ant-collapse-item
  > .ant-collapse-header
  .ant-collapse-extra {
  float: left !important;
  margin-right: 8px;
}
.nlp-engine-collapse > .ant-collapse {
  background-color: #aedef2;
}
</style>

<script>
import NLPModel from "./NLPComponents/NLPModel";
import ApiService from '@shared/services/api.service';
// import { notification } from 'ant-design-vue';
export default {
  name: "NLPDeepLearningEngine",
  components: { NLPModel },
  props: {
    nlpEngineConfiguration: {
      type: Array,
      default: () => []
    },
    nlpNotificationConfig: {
      type: Object,
      default: () => undefined
    },
    policies: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      enable_nlp_deeplearning_engine: false,
      isLoading: true,
      notification_policy: "DistilBERT+",
      distilbert_model: {
        model_name: "distilbert",
        enabled: false,
        defaced_threshold: 0.5,
        words_ignored: [],
      },
      xgboost_model: {
        model_name: "xgboost",
        enabled: false,
        defaced_threshold: 0.5,
        words_ignored: [],
      },
      updated: false,
      isEngineTrial: false,
      trialEndDate: null,
      isEngineSubscribed: false,
      showEngine: false,
      policy: [],
      policy_id: -1,
      show_model_setting: false,
    };
  },
  computed: {
    canStartFreeTrial(){
      if (this.isEngineTrial || this.isEngineSubscribed){
        return false;
      }
      return true;
    },
    featureAccess() {
      return this.$store.state.user.data.feature_access;
    },
  },
  watch: {
    nlpEngineConfiguration: {
      handler() {
        this.init_config();
        this.updates = [];
      },
      deep: true,
    },
    policies: {
      handler() {
        if (this.policies.length > 0) {
          const policy = this.policies[0];
          this.policy = policy;
          this.policy_id = policy['id'];
          if (this.nlpNotificationConfig && this.nlpNotificationConfig.enabled > 0){
            return;
          }
          // this.assign_policy_to_model(policy);
        }
      },
      deep: true,
    }
  },
  mounted() {
    this.init_config();
    this.getDatas();
    this.isEngineSubscribed = !!this.featureAccess['nlp-engine'];
    if (this.isEngineSubscribed){
      this.showEngine = true;
    }
  },
  methods: {
    assign_policy_to_model(policy){
      const nlp_notification_configuration = policy['nlp_notification_configurations'];
      if (nlp_notification_configuration !== undefined){
        switch(nlp_notification_configuration['notifyWhen']){
          case 0:
            this.notification_policy = "Either";
            break;
          case 1:
            this.notification_policy = "Both";
            break;
          case 2:
            this.notification_policy = "DistilBERT+";
            break;
          case 3:
            this.notification_policy = "XGB";
            break;
          default:
            this.notification_policy = "DistilBERT+";
            break;
        }
        this.enable_nlp_deeplearning_engine = !!nlp_notification_configuration['enabled'];
      }
      const nlp_configurations = policy['nlp_engine_configurations'];
      if (nlp_configurations !== undefined){
        nlp_configurations.forEach((config) => {
          if (config["modelName"] === "distilbert") {
            this.distilbert_model.enabled = !!config["enabled"];
            this.distilbert_model.defaced_threshold = config["defaceThreshold"];
            const words_ignored = config["wordsIgnored"];
            this.distilbert_model.words_ignored = words_ignored === '[]' ? [] : JSON.parse(words_ignored);
          } else if (config["modelName"] === "xgboost") {
            this.xgboost_model.enabled = !!config["enabled"];
            this.xgboost_model.defaced_threshold = config["defaceThreshold"];
            const words_ignored = config["wordsIgnored"];
            this.xgboost_model.words_ignored =  words_ignored === '[]' ? [] : JSON.parse(words_ignored);
          }
        });
      }
    },
    async remove_url_configurations(){
      this.isLoading = true;
      this.enable_nlp_deeplearning_engine = false;
      this.distilbert_model.enabled = false;
      this.xgboost_model.enabled = false;
      this.notification_policy = "DistilBERT+";
      this.updated = true;
      await this.$emit("save-config")
      this.isLoading = false;
      this.show_model_setting = false;
      // ApiService.post("/urls/" + this.$route.params.id +"/nlp/config/delete").then(res=>{
      //   if (res){
      //     if (res === 'success'){
      //       notification.open({
      //       message: 'The url configuration is removed!',
      //       description: 'The url configurations have been removed, the url will now follow the policy configurations.',
      //       duration: 10
      //     });
      //     }
      //   }
      // }).catch(error=>{
      //   this.$showMultiErrorMessage(error.messages);
      // })
      // this.updated = false;
    },
    formatDate(date){
      return date.getFullYear() + '-' + (date.getMonth() + 1) + '-' + date.getDate();
    },
    async getDatas() {
      this.isLoading = true;
      ApiService.get('trial_features/nlp_engine').then(res=>{
        if (res){
          this.trialEndDate = new Date(res.end_date);
          const trial_valid = this.trialEndDate > new Date();
          if (trial_valid){
            this.isEngineTrial = true;
            this.showEngine = true;
          }
        }
      }).catch(error=>{
        this.$showMultiErrorMessage(error.messages);
      }).finally(()=>{
        this.isLoading = false;
      });
    },
    // async startTrial(){
    //   this.isLoading = true;
    //   ApiService.post('trial_features/nlp_engine/start_trial').then(()=>{
    //     this.getDatas();
    //   }).catch(error=>{
    //     this.$showMultiErrorMessage(error.messages);
    //   }).finally(()=>{
    //     this.isLoading = false;
    //   });
    // },
    change_enable_nlp_deeplearning_engine(){
      this.enable_nlp_deeplearning_engine = !this.enable_nlp_deeplearning_engine;
      if (this.enable_nlp_deeplearning_engine){
        this.distilbert_model.enabled = true;
        this.xgboost_model.enabled = false;
        this.notification_policy = "DistilBERT+";
        this.show_model_setting = true;
      } else {
        this.distilbert_model.enabled = false;
        this.xgboost_model.enabled = false;
        this.notification_policy = "DistilBERT+";
        this.show_model_setting = false;
      }
      this.updated = true;
    },
    init_config() {
      if (this.nlpNotificationConfig){
        this.enable_nlp_deeplearning_engine = !!this.nlpNotificationConfig.enabled;
        this.notification_policy = this.nlpNotificationConfig.notification_policy;
        this.show_model_setting = !!this.nlpNotificationConfig.enabled;
      }
      const configs = this.format_config();
      this.assignValueToEngine(configs);
    },
    format_config() {
      const new_configurations = [];
      if (this.nlpEngineConfiguration) {
        this.nlpEngineConfiguration.forEach((config) => {
          new_configurations.push({
            model_name: config["modelName"],
            enabled: config["enabled"],
            defaced_threshold: config["defaceThreshold"],
            words_ignored: JSON.parse(config["wordsIgnored"]) === "[]" ? [] : JSON.parse(config["wordsIgnored"]),
          });
        });
      }
      return new_configurations;
    },
    assignValueToEngine(configs) {
      configs.forEach((config) => {
        if (config["model_name"] === "distilbert") {
          this.distilbert_model.enabled = !!config["enabled"];
          this.distilbert_model.defaced_threshold = config["defaced_threshold"];
          this.distilbert_model.words_ignored = config["words_ignored"];
        } else if (config["model_name"] === "xgboost") {
          this.xgboost_model.enabled = !!config["enabled"];
          this.xgboost_model.defaced_threshold = config["defaced_threshold"];
          this.xgboost_model.words_ignored = config["words_ignored"];
        }
      });
    },
    updateParent(config) {
      this.assignValueToEngine([config]);
      this.updated = true;
    },
    update_notification_policy(selection){
      switch (selection){
        case "Either":
          this.distilbert_model.enabled = true;
          this.xgboost_model.enabled = true;
          break;
        case "Both":
          this.distilbert_model.enabled = true;
          this.xgboost_model.enabled = true
          break 
        case "DistilBERT+":
          this.distilbert_model.enabled = true;
          this.xgboost_model.enabled = false;
          break
        case "XGB":
          this.distilbert_model.enabled = false;
          this.xgboost_model.enabled = true;
          break
        default:
          this.distilbert_model.enabled = true;
          this.xgboost_model.enabled = true;
          break
      }
      this.notification_policy = selection;
      this.updated = true;
    },
    async save_config(){
      this.isLoading = true;
      await this.$emit("save-config")
      this.isLoading = false;
    }
  },
};
</script>
